/**
 * InboxConversationRow — matter-aware conversation row.
 * Dense single-line (Gmail-compact): unread dot · sender · signals · subject —
 * preview · attachments · matter chip · time. A barrister triages ~35 rows per
 * screen without opening the thread. Hovering swaps time for row actions.
 */

import { useState } from 'react';
import { Star, Archive, Paperclip, Gavel, Calendar, Money } from '@phosphor-icons/react';
import type { ConversationSummary } from '@/lib/inboxApi';

const FONT = "'IBM Plex Sans', sans-serif";

/** Display name from "Name <email>" or a prettified email local-part. */
function extractName(participant: string): string {
  const m = participant.match(/^"?([^"<]+?)"?\s*<[^>]+>$/);
  if (m && m[1]) return m[1].trim();
  const local = (participant.split('@')[0] ?? participant).replace(/[._-]+/g, ' ').trim();
  return local.replace(/\b\w/g, (c) => c.toUpperCase()) || participant;
}

function participantDisplay(conv: ConversationSummary): string {
  const names = conv.participants.slice(0, 2).map(extractName);
  const display = names.filter(Boolean).join(', ');
  if (conv.participants.length > 2) return `${display} +${conv.participants.length - 2}`;
  return display || extractName(conv.lastFromAddress);
}

function timeDisplay(iso: string): string {
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return d.toLocaleDateString('en-GB', { weekday: 'short' });
  if (diffD < 365) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
}

interface Props {
  conversation: ConversationSummary;
  isSelected: boolean;
  anySelected: boolean;
  onSelect: (id: string) => void;
  onStarToggle: (id: string) => void;
  onArchive: (id: string) => void;
  onClick: (id: string) => void;
}

export function InboxConversationRow({
  conversation: conv, isSelected, anySelected,
  onSelect, onStarToggle, onArchive, onClick,
}: Props) {
  const [hovered, setHovered] = useState(false);
  const isUnread = conv.unreadCount > 0;
  const showCheckbox = hovered || anySelected || isSelected;
  const name = participantDisplay(conv);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClick(conv.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 12px', height: 32,
        borderBottom: '1px solid var(--border-subtle)',
        cursor: 'pointer', userSelect: 'none',
        background: isSelected ? 'var(--surface-selected)' : hovered ? 'var(--surface-hover)' : 'transparent',
        borderLeft: isSelected ? '2px solid #2563EB' : '2px solid transparent',
        transition: 'background 0.08s',
      }}
    >
      {/* Select ↔ unread dot */}
      <div
        onClick={(e) => { e.stopPropagation(); onSelect(conv.id); }}
        style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {showCheckbox ? (
          <div style={{
            width: 15, height: 15, borderRadius: 4,
            border: isSelected ? '2px solid #2563EB' : '1.5px solid #cbd5e1',
            background: isSelected ? '#2563EB' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isSelected && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1.5 5.5L3.5 7.5L8.5 2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        ) : (
          isUnread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563EB' }} />
        )}
      </div>

      {/* Sender (fixed) */}
      <span style={{
        width: 168, flexShrink: 0, fontFamily: FONT, fontSize: 13,
        fontWeight: isUnread ? 700 : 500, color: 'var(--text-primary)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {name}
      </span>

      {/* Signals */}
      {conv.isWithoutPrej && (
        <span title="Without prejudice" style={{
          fontFamily: FONT, fontSize: 9, fontWeight: 700, color: '#9a3412',
          background: 'rgba(249,115,22,0.15)', borderRadius: 3, padding: '1px 4px', flexShrink: 0,
        }}>
          WP
        </span>
      )}
      {conv.hasHearingRef && <Gavel size={12} weight="fill" color="#7c3aed" style={{ flexShrink: 0 }} />}
      {conv.hasDeadline && <Calendar size={12} weight="fill" color="#dc2626" style={{ flexShrink: 0 }} />}
      {conv.isBillingRelevant && <Money size={12} weight="fill" color="#059669" style={{ flexShrink: 0 }} />}

      {/* Subject — preview (single line, flexes) */}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 5, overflow: 'hidden' }}>
        <span style={{
          fontFamily: FONT, fontSize: 13, fontWeight: isUnread ? 600 : 400,
          color: isUnread ? 'var(--text-primary)' : 'var(--text-secondary)',
          flexShrink: 0, maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {conv.subject || '(no subject)'}
        </span>
        {conv.lastSnippet && (
          <span style={{
            flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 12.5, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            — {conv.lastSnippet}
          </span>
        )}
      </span>

      {/* Right cluster: attachments · matter · thread count · time / actions */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {conv.hasAttachments && <Paperclip size={12} color="#9ca3af" />}
        {conv.matter && (
          <span style={{
            padding: '1px 6px', background: 'var(--surface-selected)', color: '#2563EB',
            borderRadius: 4, fontFamily: FONT, fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            {conv.matter.caseNumber}
          </span>
        )}
        {conv.totalCount > 1 && (
          <span style={{ fontFamily: FONT, fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
            {conv.totalCount}
          </span>
        )}
        {hovered ? (
          <span style={{ display: 'flex', gap: 2 }}>
            <HoverBtn title={conv.isStarred ? 'Unstar' : 'Star'} onClick={(e) => { e.stopPropagation(); onStarToggle(conv.id); }}>
              <Star size={14} weight={conv.isStarred ? 'fill' : 'regular'} color={conv.isStarred ? '#F6C244' : undefined} />
            </HoverBtn>
            <HoverBtn title="Archive" onClick={(e) => { e.stopPropagation(); onArchive(conv.id); }}><Archive size={14} /></HoverBtn>
          </span>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {conv.isStarred && <Star size={13} weight="fill" color="#F6C244" />}
            <span style={{
              width: 44, textAlign: 'right', fontFamily: FONT, fontSize: 11.5, whiteSpace: 'nowrap',
              color: isUnread ? '#2563EB' : 'var(--text-muted)', fontWeight: isUnread ? 600 : 400,
            }}>
              {timeDisplay(conv.lastMessageAt)}
            </span>
          </span>
        )}
      </span>
    </div>
  );
}

// ─── Tiny hover icon button ──────────────────────────────────────

function HoverBtn({ title, onClick, children }: { title: string; onClick: (e: React.MouseEvent) => void; children: React.ReactNode }) {
  const [h, setH] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', borderRadius: 5, background: h ? 'var(--surface-sunken)' : 'transparent',
        color: 'var(--text-muted)', cursor: 'pointer', padding: 0, flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}
